import { formatPlainDate, isAirAdmin } from '@airreading/common';
import {
  ClassTimeType,
  formatReassignmentReason,
  GetPendingStudentGroupReassignmentRequestsSortableColumn,
  PendingStudentGroupReassignmentRequest,
  getPendingStudentGroupReassignmentRequestsSortableColumnSchema,
  getPendingStudentGroupReassignmentRequestsSortOrderSchema,
  studentGroupReassignmentRequestStatusSchema,
  studentGroupTypeSchema,
} from '@airreading/common/data';
import { useQuery } from '@tanstack/react-query';
import { createFileRoute, Link } from '@tanstack/react-router';
import { getCoreRowModel, useReactTable } from '@tanstack/react-table';
import { PencilIcon } from 'lucide-react';
import { useMemo } from 'react';
import { z } from 'zod';

import { AirIdDisplay } from '@/components/air-id-display';
import { InfoPage } from '@/components/info-page';
import { Button } from '@/components/ui/button';
import { createTableRowSelectionCell, EntityTable } from '@/components/table/entity-table';
import { TableErrorState } from '@/components/table/table-error-state';
import { TableFilterDropdown } from '@/components/table/table-filter-dropdown';
import { TableFilterInputField } from '@/components/table/table-filter-field';
import { TableFilterResetButton } from '@/components/table/table-filter-reset-button';
import { useSchoolAndOrgOptions } from '@/lib/api-call-hooks';
import { EmptyArray, getPendingStudentGroupReassignmentRequests } from '@/lib/api-calls';
import { useRowsBulkSelection } from '@/lib/bulk-selection';
import { useErrorToast } from '@/lib/error-handling';
import { usePaginatedFilters } from '@/lib/filtering';
import {
  studentGroupReassignmentRequestDisplayStatusFilterOptions,
  studentGroupTypesFilterOption,
} from '@/lib/form-utils';
import { paginationRequestSchema, DEFAULT_PAGE_SIZE } from '@/lib/pagination';
import { useNavigateSearchState, validateRouteRole } from '@/lib/routing';
import { useSorting } from '@/lib/sorting';
import { airColumnHelper } from '@/lib/tables';
import { ClassSessionsDrawer } from '@/routes/_layout/orgs/schools/-components/class-sessions-drawer';
import { BulkEditReassignmentRequest } from '@/routes/_layout/student-groups/-components/bulk-edit-reassignment-request';
import { BulkUpdateReassignmentStatusMenu } from '@/routes/_layout/student-groups/-components/bulk-update-reassignment-status';
import { ReassignTeacher } from '@/routes/_layout/student-groups/-components/reassign-teacher';
import { studentGroupsTabs } from '@/routes/_layout/student-groups/-components/tabs';
import { ReassignmentRequestDisplayStatus } from '../-components/reassignment-request-display-status';

const pendingReassignmentSearchSchema = paginationRequestSchema.extend({
  query: z.string().optional().catch(''),
  schools: z.array(z.string()).optional(),
  displayStatuses: z.array(studentGroupReassignmentRequestStatusSchema).optional(),
  types: z.array(studentGroupTypeSchema).optional(),
  sortBy: getPendingStudentGroupReassignmentRequestsSortOrderSchema.optional(),
});

export const Route = createFileRoute('/_layout/student-groups/pending-reassignment/')({
  beforeLoad: validateRouteRole(isAirAdmin),
  component: PendingReassignmentIndex,
  validateSearch: pendingReassignmentSearchSchema,
});

const col = airColumnHelper<PendingStudentGroupReassignmentRequest>(
  getPendingStudentGroupReassignmentRequestsSortableColumnSchema,
);

function formatCoachName(coach: PendingStudentGroupReassignmentRequest['originalCoach']) {
  return coach?.fullNameOrEmail ?? '-';
}

const columns = [
  createTableRowSelectionCell(col),

  col.accessor((row) => row, {
    id: 'reassignment-request',
    header: '',
    size: 50,
    cell: ({ getValue }) => (
      <ReassignTeacher
        reassignmentRequest={getValue()}
        trigger={
          <Button variant="ghost" size="icon" aria-label="Reassign teacher">
            <PencilIcon size={18} />
          </Button>
        }
      />
    ),
  }),

  col.accessor('studentGroupId', {
    id: 'classId',
    header: 'Class ID',
    enableSorting: false,
    cell: ({ getValue }) => {
      const studentGroupId = getValue();
      return (
        <AirIdDisplay
          entityType="cls"
          rawId={studentGroupId}
          showCopyButton
          wrapper={(displayId) => (
            <ClassSessionsDrawer
              trigger={
                <Button variant="link" className="p-0 h-auto text-blue-600 hover:text-blue-800 no-underline">
                  {displayId}
                </Button>
              }
              classId={studentGroupId}
            />
          )}
        />
      );
    },
  }),

  col.accessor('displayStatus', {
    id: 'reassignmentStatus',
    header: 'Reassignment status',
    cell: ({ getValue }) => <ReassignmentRequestDisplayStatus displayStatus={getValue()} />,
  }),

  col.accessor('school', {
    id: 'school',
    header: 'School - District',
    enableSorting: false,
    cell: ({ getValue, row }) => {
      const school = getValue();
      const schoolClassesPath =
        row.original.classTimeType === ClassTimeType.one_time
          ? '/orgs/schools/$schoolId/one-time-classes'
          : '/orgs/schools/$schoolId/classes';

      return (
        <Link
          to={schoolClassesPath}
          params={{ schoolId: school.id }}
          search={{ pageIndex: 0, pageSize: DEFAULT_PAGE_SIZE }}
          className="text-blue-600 text-nowrap hover:underline"
        >
          {school.name} – {school.org.name}
        </Link>
      );
    },
  }),

  col.accessor('originalCoach', {
    id: 'originalCoach',
    header: 'Teacher needs reassign',
    cell: ({ getValue }) => <span>{formatCoachName(getValue())}</span>,
  }),

  col.accessor('preferredCoach', {
    id: 'preferredCoach',
    header: 'Preferred new teacher',
    enableSorting: false,
    cell: ({ getValue }) => <span>{formatCoachName(getValue())}</span>,
  }),

  col.accessor('reassignedCoach', {
    id: 'reassignedCoach',
    header: 'Reassigned new teacher',
    enableSorting: false,
    cell: ({ getValue }) => <span>{formatCoachName(getValue())}</span>,
  }),

  col.accessor('requestPlainDate', {
    id: 'requestDate',
    header: 'Request date',
    cell: ({ getValue }) => <span>{formatPlainDate(getValue())}</span>,
  }),

  col.accessor('effectivePlainDate', {
    id: 'effectiveDate',
    header: 'Effective date',
    cell: ({ getValue }) => <span>{formatPlainDate(getValue())}</span>,
  }),

  col.accessor('requester', {
    id: 'requester',
    header: 'Requester',
    enableSorting: false,
    cell: ({ getValue }) => <span>{getValue().fullNameOrEmail}</span>,
  }),

  col.accessor((row) => row, {
    id: 'reasons',
    header: 'Reassignment reason',
    enableSorting: false,
    cell: ({ getValue }) => <span>{formatReassignmentReason(getValue())}</span>,
  }),

  col.accessor('specialRequirements', {
    id: 'specialRequirements',
    header: 'Special requirements',
    enableSorting: false,
    cell: ({ getValue }) => <span>{getValue()?.trim() || '-'}</span>,
  }),
];

function PendingReassignmentIndex() {
  const {
    pageIndex,
    pageSize,
    onPaginationChange,
    filters: {
      query: [[nameQuery, activeNameQuery], setNameQuery],
      schools: [[selectedSchools, activeSelectedSchools], setSelectedSchools],
      displayStatuses: [[selectedDisplayStatuses, activeSelectedDisplayStatuses], setSelectedDisplayStatuses],
      types: [[selectedTypes, activeSelectedTypes], setSelectedTypes],
    },
    resetFilters,
  } = usePaginatedFilters(Route.id, ['query', 'schools', 'displayStatuses', 'types']);

  const [sortByFromSearch, didChangeSortBy] = useNavigateSearchState(Route.id, 'sortBy');
  const effectiveSortBy = sortByFromSearch ?? ['effectiveDate-asc'];
  const { sorting, ...tableSortingOptions } = useSorting<GetPendingStudentGroupReassignmentRequestsSortableColumn>([
    effectiveSortBy,
    didChangeSortBy,
  ]);

  const allSchoolOptions = useSchoolAndOrgOptions({ orgIds: [] });

  const {
    data: response,
    isPending,
    error,
    refetch,
  } = useQuery(
    getPendingStudentGroupReassignmentRequests({
      pageIndex,
      pageSize,
      filters: {
        query: activeNameQuery,
        schools: activeSelectedSchools,
        displayStatuses: activeSelectedDisplayStatuses,
        types: activeSelectedTypes,
      },
      sortBy: effectiveSortBy,
    }),
  );

  const reassignmentRequests = useMemo(() => response?.reassignmentRequests ?? EmptyArray, [response]);
  useErrorToast(error, 'Error loading pending reassignment requests. Please try again.');

  const { rowSelection, rowDataSelection, onRowSelectionChange, getRowId, resetAllRowSelection } =
    useRowsBulkSelection<PendingStudentGroupReassignmentRequest>({
      getRowId: (row) => row.id,
      rowIdToData: (id) => reassignmentRequests.find((request) => request.id === id),
    });

  const table = useReactTable({
    data: reassignmentRequests,
    columns,
    state: {
      pagination: {
        pageIndex,
        pageSize,
      },
      rowSelection,
      sorting,
    },
    rowCount: response?.total ?? 0,
    getCoreRowModel: getCoreRowModel(),
    getRowId,
    onPaginationChange,
    onRowSelectionChange,
    manualPagination: true,
    ...tableSortingOptions,
    enableColumnResizing: true,
  });

  const filterLeftComponents = useMemo(
    () => (
      <>
        <TableFilterInputField
          placeholder="Search by teacher, student, or class ID"
          value={nameQuery ?? ''}
          onChange={(event) => {
            setNameQuery(event.target.value);
          }}
        />
        <TableFilterDropdown
          title="School"
          options={allSchoolOptions}
          selectedValues={selectedSchools ?? []}
          onChange={setSelectedSchools}
        />
        <TableFilterDropdown
          title="Reassignment status"
          options={studentGroupReassignmentRequestDisplayStatusFilterOptions}
          selectedValues={selectedDisplayStatuses ?? []}
          onChange={setSelectedDisplayStatuses}
        />
        <TableFilterDropdown
          title="Type"
          options={studentGroupTypesFilterOption}
          selectedValues={selectedTypes ?? []}
          onChange={setSelectedTypes}
        />
        <TableFilterResetButton show onClick={resetFilters} />
      </>
    ),
    [
      nameQuery,
      setNameQuery,
      allSchoolOptions,
      selectedSchools,
      setSelectedSchools,
      selectedDisplayStatuses,
      setSelectedDisplayStatuses,
      selectedTypes,
      setSelectedTypes,
      resetFilters,
    ],
  );

  const bulkActionsMenuItems = useMemo(
    () => (
      <>
        <BulkUpdateReassignmentStatusMenu reassignmentRequests={rowDataSelection} onSuccess={resetAllRowSelection} />
        <BulkEditReassignmentRequest reassignmentRequests={rowDataSelection} onSuccess={resetAllRowSelection} />
      </>
    ),
    [resetAllRowSelection, rowDataSelection],
  );

  return (
    <InfoPage headerText="Student groups" tabs={studentGroupsTabs('Pending reassignment')}>
      <EntityTable
        table={table}
        showPagination
        isLoading={isPending}
        hasError={!!error}
        emptyState="No pending reassignment requests found"
        errorState={<TableErrorState refetchAction={refetch} />}
        bulkActionsMenuItems={bulkActionsMenuItems}
        filters={{ filterLeftComponents }}
        autoManagedColumnIds={['selection', 'reassignment-request']}
      />
    </InfoPage>
  );
}
